import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Flex, InputRef, Table, Typography } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { getColumnSearchProps } from 'src/libs/tableFunctions'
import { Guard } from 'src/features/session/guard'
import { path } from 'src/libs'
import {
  useGoldenMayStatus,
  useWinnersList,
} from 'src/pages/goldenMay2026/winners/hooks'
import {
  changeStatusSubmit,
  goldenMayStatusLoad,
  onChangeWinnerList,
} from 'src/pages/goldenMay2026/winners/model'
import { Winner } from 'src/pages/goldenMay2026/winners/types'
import {
  prizeTypesOptions,
  PRIZE_TYPES,
} from 'src/pages/goldenMay2026/constants'
import { AddWinnersModal } from 'src/pages/goldenMay2026/winners/widgets'

export const WinnersList = () => {
  const [isAddWinnersModalOpen, setIsAddWinnersModalOpen] = useState(false)
  const [searchText, setSearchText] = useState<{
    [key: string]: string
  }>({})
  const searchInput = useRef<InputRef>(null)

  const { winners, totalCountOfWinners, loading } = useWinnersList()
  const goldenMayStatus = useGoldenMayStatus()

  const { Text } = Typography

  useEffect(() => {
    goldenMayStatusLoad()
  }, [])

  useEffect(() => {
    onChangeWinnerList({
      searchText,
      page: 1,
    })
  }, [searchText])

  const columns: ColumnsType<Winner> = [
    {
      title: 'ID',
      dataIndex: ['client', 'clientId'],
      ...getColumnSearchProps<Winner>(
        'client.clientId',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (id) => (
        <Link to={path.clients.details(id)} style={{ textDecoration: 'none' }}>
          {id}
        </Link>
      ),
    },
    {
      title: 'Name',
      dataIndex: ['client', 'fullName'],
      ...getColumnSearchProps<Winner>(
        'client.fullName',
        setSearchText,
        searchText,
        searchInput,
      ),
    },
    {
      title: 'Email',
      dataIndex: ['client', 'email'],
      ...getColumnSearchProps<Winner>(
        'client.email',
        setSearchText,
        searchText,
        searchInput,
      ),
    },
    {
      title: 'Ticket',
      dataIndex: 'ticketId',
      ...getColumnSearchProps<Winner>(
        'ticketId',
        setSearchText,
        searchText,
        searchInput,
      ),
    },
    {
      title: 'Prize',
      dataIndex: 'prizeType',
      render: (type: string) =>
        PRIZE_TYPES[type as keyof typeof PRIZE_TYPES] ?? type,
      filters: prizeTypesOptions.map(({ label, value }) => ({
        text: label,
        value,
      })),
    },
  ]
  const currentStatus = goldenMayStatus[0]?.status
  return (
    <Flex vertical gap="middle">
      {currentStatus && (
        <Text style={{ fontSize: 16 }}>{`Status: ${currentStatus}`}</Text>
      )}
      {currentStatus === 'ended' && (
        <Guard permission="golden_may_2026_winners">
          <Flex justify="flex-start" gap="middle">
            <Button
              color="geekblue"
              type="primary"
              size="large"
              onClick={() => setIsAddWinnersModalOpen(true)}
            >
              Add winners
            </Button>
            <Button
              color="geekblue"
              type="primary"
              size="large"
              onClick={() => {
                if (window.confirm('Are you sure?')) {
                  changeStatusSubmit()
                }
              }}
            >
              Move to Results
            </Button>
          </Flex>
        </Guard>
      )}
      <Table
        dataSource={winners}
        bordered={true}
        className="overflow-auto"
        loading={loading}
        rowKey={(record) => record.ticketId}
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfWinners,
        }}
        onChange={(pagination, filters, sorter: any) =>
          onChangeWinnerList({
            page: pagination.current,
            limit: pagination.pageSize,
            orderBy: sorter?.field || sorter?.columnKey,
            orderDirection: sorter?.order,
            filters,
          })
        }
      />
      <AddWinnersModal
        isVisible={isAddWinnersModalOpen}
        onClose={() => setIsAddWinnersModalOpen(false)}
      />
    </Flex>
  )
}
